import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import { Film, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useProject } from "../../common/contexts/ProjectContext";
import { useTheme } from "../../common/contexts/ThemeContext";
import { truncateString } from "../../common/utils/format_utils";
import { resolveImageUrl } from "../../common/utils/imageUrl";

const AssetCard = ({ asset, isWrapped = false }) => {
  const { t } = useTranslation("assets");
  const navigate = useNavigate();
  const { currentUserRole } = useProject();
  const { colors } = useTheme();
  const isOwner = currentUserRole === "owner";

  const card = (
    <Card
      variant="outlined"
      xs={{ borderRadius: 3 }}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "border-color 0.3s ease",
        "&:hover": {
          borderColor: colors.text.secondary,
        },
      }}
    >
      {asset.thumbnail ? (
        <CardMedia
          component="img"
          image={resolveImageUrl(asset.thumbnail)}
          alt={asset.title}
          onClick={() => navigate(`/assets/${asset.id}`)}
          sx={{
            borderRadius: 1,
            height: { xs: 200, sm: 180 },
            objectFit: "cover",
            cursor: "pointer",
          }}
        />
      ) : (
        <Box
          onClick={() => navigate(`/assets/${asset.id}`)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: { xs: 200, sm: 180 },
            backgroundColor: colors.secondary,
            borderRadius: 1,
            cursor: "pointer",
          }}
        >
          <Package
            size={64}
            style={{
              color: colors.text.secondary,
            }}
          />
        </Box>
      )}
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          justifyContent: "space-between",
        }}
      >
        <Typography
          component={Link}
          to={"/assets/" + asset.id}
          variant="bodyLg"
          sx={{
            textDecoration: "none",
            color: "inherit",
            fontWeight: "600",
          }}
        >
          {truncateString(asset.title, 53)}
        </Typography>

        <Typography
          variant="bodyMd"
          color="textSecondary"
          sx={{
            mt: 1,
            minHeight: "2.5em",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {asset.description &&
          asset.description !== "null" &&
          asset.description !== "None" &&
          asset.description.trim() !== ""
            ? asset.description
            : t("noDescription")}
        </Typography>

        {asset.asset_type === "series" && asset.episode_count > 0 && (
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Film size={14} style={{ color: colors.text.secondary }} />
            <Typography variant="caption" color="textSecondary">
              {t("episodeCount", { count: asset.episode_count })}
            </Typography>
          </Box>
        )}

        {isOwner && asset.producers?.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {asset.producers.slice(0, 3).map((p) => (
              <Chip
                key={p.user_id}
                label={p.name}
                size="small"
                variant="outlined"
              />
            ))}
            {asset.producers.length > 3 && (
              <Chip
                label={`+${asset.producers.length - 3}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (isWrapped) return card;

  return <Grid size={{ md: 4, xs: 12 }}>{card}</Grid>;
};

export default AssetCard;
